import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { EPGChannel, EPGChannels } from 'custom_modules/react-native-epg';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { RBChannel } from 'store/railsBuilder/reducer.types';
import { Entitlement } from 'store/user/reducer.types';

import isSubscribedToChannel from 'utils/isSubscribedToChannel';
import { adjustString } from 'utils/strings';

import { EpgChannelExtra, EpgFilterCategory } from 'screens/Epg/types';

type Params = {
    channels: RBChannel[];
    filterCategory: EpgFilterCategory | undefined;
    entitlements: Entitlement[];
};

const {
    EPG: {
        FILTER_CATEGORIES: {
            EPG_FILTER_ORIGINAL,
            EPG_FILTER_A_TO_Z,
            EPG_FILTER_SUBSCRIBED,
        },
    },
} = LANGUAGE_KEYS;

function useChannels({
    channels,
    filterCategory,
    entitlements,
}: Params): EPGChannels<EpgChannelExtra> {
    const { i18n } = useTranslation();

    return useMemo(() => {
        if (
            !filterCategory?.key ||
            filterCategory.key === EPG_FILTER_ORIGINAL
        ) {
            return channels.map((_channel) =>
                createEpgChannel(_channel, entitlements),
            );
        }

        if (filterCategory.key === EPG_FILTER_A_TO_Z) {
            return sortAlphabetically(channels).map((_channel) =>
                createEpgChannel(_channel, entitlements),
            );
        }

        if (filterCategory.key === EPG_FILTER_SUBSCRIBED) {
            return channels
                .map((_channel) => createEpgChannel(_channel, entitlements))
                .filter(({ extra }) => extra!.subscribed);
        }

        const hungarianFilterCategoryValue = i18n.getResource(
            'hu',
            'translation',
            filterCategory.key,
        );

        return channels.reduce<EPGChannels<EpgChannelExtra>>(
            (acc, _channel) => {
                if (
                    _channel.editorial.Categories.includes(
                        hungarianFilterCategoryValue,
                    )
                ) {
                    return [...acc, createEpgChannel(_channel, entitlements)];
                }

                return acc;
            },
            [],
        );
    }, [filterCategory?.key, i18n, channels, entitlements]);
}

export default useChannels;

function createEpgChannel(
    channel: RBChannel,
    entitlements: Entitlement[],
): EPGChannel<EpgChannelExtra> {
    return {
        id: channel.editorial.id,
        name: channel.editorial.longName,
        number: channel.editorial.tvChannel,
        extra: {
            subscribed: isSubscribedToChannel(channel, entitlements),
        },
    };
}

function sortAlphabetically(channels: RBChannel[]) {
    return [...channels].sort(
        (
            { editorial: { longName: longName1 } },
            { editorial: { longName: longName2 } },
        ) => {
            return adjustString(longName1).localeCompare(
                adjustString(longName2),
            );
        },
    );
}
